import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const SESSION_INITIAL_VERSION = 'app_session_initial_version';
const LATEST_SERVER_VERSION = 'app_latest_server_version';
const DISMISSED_VERSION = 'app_dismissed_version';

const getStoredItem = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const setStoredItem = (key, value) => {
  try {
    sessionStorage.setItem(key, value);
  } catch (e) {
    // Ignore storage quota / security exceptions
  }
};

const removeStoredItem = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch (e) {
    // Ignore storage exceptions
  }
};

export const useVersionCheck = () => {
  const location = useLocation();
  const isDev = process.env.NODE_ENV === 'development';

  const isCheckingRef = useRef(false);

  const [initialVersion, setInitialVersion] = useState(() => getStoredItem(SESSION_INITIAL_VERSION));
  const [latestServerVersion, setLatestServerVersion] = useState(() => getStoredItem(LATEST_SERVER_VERSION));
  const [dismissedVersion, setDismissedVersion] = useState(() => getStoredItem(DISMISSED_VERSION));
  const [isReloading, setIsReloading] = useState(false);

  const activeInitial = initialVersion || getStoredItem(SESSION_INITIAL_VERSION);
  const activeLatest = latestServerVersion || getStoredItem(LATEST_SERVER_VERSION);
  const activeDismissed = dismissedVersion || getStoredItem(DISMISSED_VERSION);

  const hasNewVersion =
    !isDev &&
    Boolean(activeLatest) &&
    Boolean(activeInitial) &&
    activeLatest !== activeInitial;

  const isDismissed = Boolean(activeDismissed) && activeDismissed === activeLatest;

  const fetchLatestVersion = async () => {
    if (isDev) return null;

    try {
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
        },
      });

      const contentType = response.headers.get('content-type') || '';
      if (response.ok && contentType.includes('application/json')) {
        const data = await response.json();
        if (data?.version) return String(data.version);
      }
    } catch (e) {
      // Ignore network failures
    }

    return null;
  };

  const checkVersion = async () => {
    if (isDev || isCheckingRef.current) return;
    isCheckingRef.current = true;

    try {
      const serverVersion = await fetchLatestVersion();

      if (serverVersion) {
        let currentInitial = getStoredItem(SESSION_INITIAL_VERSION);
        if (!currentInitial) {
          currentInitial = serverVersion;
          setStoredItem(SESSION_INITIAL_VERSION, serverVersion);
          setInitialVersion(serverVersion);
        }

        if (currentInitial !== serverVersion) {
          console.info(
            `[VersionCheck] New build version detected (${serverVersion} vs ${currentInitial}).`
          );
          setStoredItem(LATEST_SERVER_VERSION, serverVersion);
          setLatestServerVersion(serverVersion);
        }
      }
    } catch (error) {
      console.error('[VersionCheck] Error checking version:', error);
    } finally {
      isCheckingRef.current = false;
    }
  };

  const handleReload = () => {
    setIsReloading(true);
    removeStoredItem(SESSION_INITIAL_VERSION);
    removeStoredItem(LATEST_SERVER_VERSION);
    removeStoredItem(DISMISSED_VERSION);
    window.location.reload();
  };

  const handleDismiss = () => {
    const targetVersion = activeLatest || latestServerVersion;
    if (targetVersion) {
      setStoredItem(DISMISSED_VERSION, targetVersion);
      setDismissedVersion(targetVersion);
    }
  };

  // Check version on initial load
  useEffect(() => {
    if (isDev) return;
    checkVersion();
  }, []);

  // Check version on route / page change
  useEffect(() => {
    if (isDev) return;
    checkVersion();
  }, [location.pathname, location.search]);

  // Check version on tab focus & visibility change
  useEffect(() => {
    if (isDev) return;
    const handleFocus = () => {
      checkVersion();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return {
    hasNewVersion,
    isDismissed,
    isReloading,
    onReload: handleReload,
    onDismiss: handleDismiss,
  };
};

export default useVersionCheck;

